import { clsx } from "clsx";
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  type ChangeEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
} from "react";
import { IconSearch, IconX } from "@tabler/icons-react";
import { Text } from "../typography";

export interface SearchBarProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "onChange"> {
  value?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** When provided, Enter submits and — unless `showSearchButton` is false — a trailing button is rendered. */
  onSearch?: (value: string) => void;
  /** When provided, a clear (X) button shows while the field has a value; it calls this and refocuses the field. */
  onClear?: () => void;
  /** Button label when the search button is rendered. */
  searchLabel?: string;
  /** Set to false to hide the trailing button while keeping Enter-to-submit behavior. Defaults to true. */
  showSearchButton?: boolean;
  wrapperClassName?: string;
}

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(function SearchBar(
  {
    value,
    onChange,
    onSearch,
    onClear,
    searchLabel = "Search",
    showSearchButton = true,
    placeholder = "Search",
    wrapperClassName,
    className,
    ...rest
  },
  ref,
) {
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current!, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && onSearch) {
      onSearch(e.currentTarget.value);
    }
    rest.onKeyDown?.(e);
  };

  const handleClear = () => {
    onClear?.();
    inputRef.current?.focus();
  };

  return (
    <div
      className={clsx(
        "flex h-12 items-center gap-3 rounded-2xl border border-borderDefault bg-bgWhite pl-3 pr-2",
        "focus-within:border-textBody transition-colors",
        wrapperClassName,
      )}
    >
      <IconSearch size={18} className="shrink-0 text-textMuted" />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={clsx(
          "min-w-0 flex-1 bg-transparent outline-none",
          "text-bodySm font-bold text-textDark",
          "placeholder:font-normal placeholder:text-textMuted",
          className,
        )}
        {...rest}
      />
      {onClear && value ? (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className="inline-flex size-6 shrink-0 items-center justify-center text-textMuted transition-colors hover:text-textDark"
        >
          <IconX size={18} />
        </button>
      ) : null}
      {onSearch && showSearchButton ? (
        <button
          type="button"
          onClick={() => onSearch(value ?? "")}
          className="rounded-xl bg-brandBlack px-6 py-2 text-textOnDark transition-opacity hover:opacity-90"
        >
          <Text variant="button" inline className="text-textOnDark">
            {searchLabel}
          </Text>
        </button>
      ) : null}
    </div>
  );
});
